import React, { useState } from 'react';
import { ArrowRight, Search, Filter, Calendar, Clock, BookOpen } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { getAllBlogs, BlogPost } from '../data/blogs';
import SEOHead from '../components/SEOHead';

const categories = [
  'All',
  'AI Ethics',
  'Data Governance', 
  'Automation',
  'Healthcare',
  'Manufacturing',
  'Financial Services'
];

const tags = [
  'AI',
  'Machine Learning',
  'Automation',
  'Data Engineering',
  'Healthcare',
  'Manufacturing',
  'ROI',
  'Digital Transformation'
];

const caseStudies = [
  {
    icon: ArrowRight,
    title: 'Intelligent Automation Transforms Manufacturing Operations',
    client: 'A large manufacturing company with global operations',
    challenge: `The client's order fulfillment and inventory management processes were heavily manual and error-prone, leading to shipping delays, high labor costs, and frequent inventory inaccuracies.`,
    solution: 'Implemented comprehensive automation solution with custom software bots for end-to-end order processing and AI-powered quality control routines.',
    results: [
      'Orders processed 5x faster with virtually zero errors',
      '30% staff reallocation to strategic tasks',
      '70% reduction in order processing costs',
      '25% reduction in inventory holding costs',
      'Eliminated stockouts and overstocks',
    ],
    image: 'https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?auto=format&fit=crop&q=80',
    category: 'automation',
    tags: ['automation', 'manufacturing', 'roi'],
    author: {
      name: 'Dr. Sarah Chen',
      role: 'Chief AI Officer',
      bio: 'Ph.D. in Machine Learning with 15+ years of experience in AI implementation and enterprise digital transformation.',
      avatar: 'https://images.unsplash.com/photo-1560250097-0b93528c311a?auto=format&fit=crop&q=80'
    }
  },
  {
    icon: ArrowRight,
    title: 'Predictive Analytics Boosts Retail Forecasting',
    client: 'A mid-sized retail chain with hundreds of stores and e-commerce platform',
    challenge: 'Unpredictable demand patterns led to frequent stockouts of popular items and excess inventory of others, impacting sales and tying up capital.',
    solution: 'Deployed AI Forecasting & Predictive Analytics using machine learning models analyzing multiple data sources for accurate demand prediction.',
    results: [
      '50% reduction in forecast error rates',
      '15% increase in revenue for in-demand items',
      '20% reduction in excess stock levels',
      '10% reduction in labor overtime expenses',
      'Enhanced promotion effectiveness',
    ],
    image: 'https://images.unsplash.com/photo-1441986300917-64674bd600d8?auto=format&fit=crop&q=80',
    category: 'ai-ethics',
    tags: ['ai', 'machine-learning', 'retail'],
    author: {
      name: 'Michael Rodriguez',
      role: 'Head of Data Science',
      bio: 'Expert in predictive modeling and large-scale data analytics solutions with proven track record at Fortune 500 companies.',
      avatar: 'https://images.unsplash.com/photo-1519345182560-3f2917c472ef?auto=format&fit=crop&q=80'
    }
  },
  {
    icon: ArrowRight,
    title: 'Data Engineering Enables Financial Insights in Real-Time',
    client: 'A regional financial services company offering banking and insurance products',
    challenge: 'Data siloed across multiple systems made consolidated reporting a manual, time-consuming process, with weeks-old data hindering swift decision-making.',
    solution: 'Built modern Data Reporting & Engineering solution with centralized cloud data warehouse and automated ETL pipelines for real-time insights.',
    results: [
      'Real-time access to key metrics',
      'Issues spotted 2-3 weeks earlier',
      '80% reduction in routine reporting time',
      'Saved $1M through early churn detection',
      'Enhanced data-driven culture',
    ],
    image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&q=80',
    category: 'data-governance',
    tags: ['data-engineering', 'financial-services', 'real-time'],
    author: {
      name: 'Emma Thompson',
      role: 'Director of Data Engineering',
      bio: 'Pioneering data architecture solutions for enterprise clients with focus on scalable, real-time data processing.',
      avatar: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?auto=format&fit=crop&q=80'
    }
  },
];

const Insights = () => {
  const location = useLocation();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [filteredInsights, setFilteredInsights] = useState(caseStudies);
  const [filteredBlogs, setFilteredBlogs] = useState<BlogPost[]>([]);
  const [availableTags, setAvailableTags] = useState<string[]>(['All']);
  const [forceUpdate, setForceUpdate] = useState(0);

  // Get fresh blogs on every navigation
  const allBlogs = getAllBlogs();
  const todaysBlog = allBlogs.length > 0 ? allBlogs[0] : null;

  // Force reload on navigation
  React.useEffect(() => {
    // Immediate state update
    const blogs = getAllBlogs();
    setFilteredBlogs(blogs);
    setSearchTerm('');
    setSelectedCategory('All');
    setSelectedTags([]);

    // Extract tags
    const uniqueTags = new Set<string>();
    blogs.forEach(blog => {
      blog.tags.forEach(tag => {
        if (tag && tag.trim()) {
          uniqueTags.add(tag);
        }
      });
    });
    setAvailableTags(['All', ...Array.from(uniqueTags).sort()]);

    // Force component update
    setForceUpdate(prev => prev + 1);

  }, [location.pathname, location.key, location.search]);

  const handleSearch = (term: string) => {
    setSearchTerm(term);
    filterContent(term, selectedCategory, selectedTags);
  };

  const handleCategoryFilter = (category: string) => {
    setSelectedCategory(category);
    filterContent(searchTerm, category, selectedTags);
  };

  const handleTagFilter = (tag: string) => {
    const newTags: string[] = tag === 'All' ? [] : [tag];
    setSelectedTags(newTags);
    filterContent(searchTerm, selectedCategory, newTags);
  };

  const filterContent = (search: string, category: string, tags: string[]) => {
    // Filter case studies
    let filteredCaseStudies = caseStudies;

    if (search) {
      filteredCaseStudies = filteredCaseStudies.filter(insight =>
        insight.title.toLowerCase().includes(search.toLowerCase()) ||
        insight.challenge.toLowerCase().includes(search.toLowerCase()) ||
        insight.solution.toLowerCase().includes(search.toLowerCase())
      );
    }

    if (category !== 'All') {
      filteredCaseStudies = filteredCaseStudies.filter(insight =>
        insight.category === category.toLowerCase().replace(' ', '-')
      );
    }

    if (tags.length > 0) {
      filteredCaseStudies = filteredCaseStudies.filter(insight =>
        tags.some(tag => insight.tags.includes(tag.toLowerCase().replace(' ', '-')))
      );
    }

    setFilteredInsights(filteredCaseStudies);

    // Filter blog posts
    let filteredBlogPosts = allBlogs;

    if (search) {
      filteredBlogPosts = filteredBlogPosts.filter(blog =>
        blog.title.toLowerCase().includes(search.toLowerCase()) ||
        blog.excerpt.toLowerCase().includes(search.toLowerCase()) ||
        blog.tags.some(tag => tag.toLowerCase().includes(search.toLowerCase()))
      );
    }

    if (category !== 'All') {
      filteredBlogPosts = filteredBlogPosts.filter(blog =>
        blog.category.toLowerCase() === category.toLowerCase() ||
        blog.category.toLowerCase().replace(' ', '-') === category.toLowerCase().replace(' ', '-')
      );
    }

    if (tags.length > 0) {
      filteredBlogPosts = filteredBlogPosts.filter(blog =>
        tags.some(tag =>
          blog.tags.some(blogTag =>
            blogTag.toLowerCase().includes(tag.toLowerCase().replace(' ', '-')) ||
            blogTag.toLowerCase().replace(' ', '-') === tag.toLowerCase().replace(' ', '-')
          )
        )
      );
    }

    setFilteredBlogs(filteredBlogPosts);
  };

  return (
    <>
      <SEOHead
        title="Insights & Blog | Agility AI"
        description="Expert insights on AI, automation, data engineering, and digital transformation. Stay updated with latest trends."
        url="https://www.agilitytech.ai/insights"
      />
    <div className="pt-16">
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-r from-blue-900 to-black overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1451187580459-43490279c0fa?auto=format&fit=crop&q=80"
            alt="Insights Banner"
            className="w-full h-full object-cover opacity-20"
          />
        </div>
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-3xl fade-in-up">
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 leading-tight">
              Insights & Resources
            </h1>
            <h2 className="text-2xl md:text-3xl text-teal-400 font-semibold mb-6">
              Driving Innovation Through Knowledge
            </h2>
            <p className="text-xl text-gray-300 leading-relaxed">
              Explore our latest thinking on AI, automation, and digital transformation. From detailed 
              case studies to forward-looking analysis, find the insights you need to stay ahead in 
              a rapidly evolving technological landscape.
            </p>
          </div>
        </div>
      </section>

      {/* Search and Filter Section */}
      <section className="py-12 bg-gray-900">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold text-white mb-6 text-center">Find Relevant Insights</h2>
            <p className="text-gray-400 text-center mb-8">Filter our content by category, tags, or search for specific topics</p>
            
            {/* Search Bar */}
            <div className="relative mb-8">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                placeholder="Search insights, case studies, and articles..."
                value={searchTerm}
                onChange={(e) => handleSearch(e.target.value)}
                className="w-full pl-10 pr-4 py-3 bg-gray-800 border border-gray-700 rounded-lg 
                  text-white placeholder-gray-400 focus:ring-2 focus:ring-teal-400 focus:border-transparent"
              />
            </div>

            {/* Category Filters */}
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
                <Filter className="h-5 w-5 mr-2" />
                Categories
              </h3>
              <div className="flex flex-wrap gap-3">
                {categories.map((category) => (
                  <button
                    key={category}
                    onClick={() => handleCategoryFilter(category)}
                    className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                      selectedCategory === category
                        ? 'bg-teal-500 text-white'
                        : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                    }`}
                  >
                    {category}
                  </button>
                ))}
              </div>
            </div>

            {/* Tag Filters */}
            <div className="mb-8">
              <h3 className="text-lg font-semibold text-white mb-4">Filter by Tags</h3>
              <div className="relative">
                <select
                  value={selectedTags.length === 0 ? 'All' : selectedTags[0]}
                  onChange={(e) => handleTagFilter(e.target.value)}
                  className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg
                    text-white appearance-none cursor-pointer focus:ring-2 focus:ring-teal-400 focus:border-transparent"
                >
                  {availableTags.map((tag) => (
                    <option key={tag} value={tag} className="bg-gray-800 text-white">
                      {tag === 'All' ? 'All Tags' : `#${tag}`}
                    </option>
                  ))}
                </select>
                <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                  <Filter className="h-5 w-5 text-gray-400" />
                </div>
              </div>
              {selectedTags.length > 0 && (
                <div className="mt-3 flex items-center gap-2">
                  <span className="text-sm text-gray-400">Active filter:</span>
                  <span className="px-3 py-1 bg-teal-500 text-white rounded-full text-sm font-medium">
                    #{selectedTags[0]}
                  </span>
                  <button
                    onClick={() => handleTagFilter('All')}
                    className="text-sm text-gray-400 hover:text-white transition-colors"
                  >
                    Clear
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Insight of the Day */}
      <section className="py-16 bg-black" style={{ minHeight: '400px' }}>
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-white mb-8 text-center fade-in-up">Insight of the Day</h2>
          <div className="max-w-6xl mx-auto">
            {todaysBlog ? (
              <Link
                to={`/insights/${todaysBlog.slug}`}
                className="block bg-gray-900/50 backdrop-blur-sm rounded-2xl shadow-xl overflow-hidden
                  border border-gray-800/50 hover:bg-gray-900/70 transition-all duration-300 card-hover fade-in-up"
              >
                <div className="md:flex">
                  <div className="md:w-2/5">
                    <div className="h-64 md:h-full relative bg-gray-800">
                      <img
                        src={todaysBlog.featuredImage}
                        alt={todaysBlog.title}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.currentTarget.onerror = null;
                          e.currentTarget.src = 'https://images.pexels.com/photos/8386440/pexels-photo-8386440.jpeg?auto=compress&cs=tinysrgb&w=1920&h=1080';
                        }}
                        loading="lazy"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent" />
                    </div>
                  </div>
                  <div className="md:w-3/5 p-8">
                    <div className="flex items-center mb-4">
                      <span className="bg-teal-500/10 text-teal-400 px-3 py-1 rounded-full text-sm font-medium mr-3">
                        Today's Insight
                      </span>
                      <span className="bg-gray-800 text-gray-300 px-3 py-1 rounded-full text-sm font-medium mr-3">
                        {todaysBlog.category}
                      </span>
                    </div>

                    <h3 className="text-2xl font-bold text-white mb-4 group-hover:text-teal-400 transition-colors">
                      {todaysBlog.title}
                    </h3>

                    <p className="text-gray-300 mb-4 line-clamp-3">
                      {todaysBlog.excerpt}
                    </p>

                    {/* Meta Info */}
                    <div className="flex items-center gap-6 text-gray-400 text-sm mb-4 p-4 bg-gray-800/50 rounded-lg">
                      <div className="flex items-center space-x-2">
                        <Calendar className="h-4 w-4 text-teal-400" />
                        <span>{new Date(todaysBlog.publishDate).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Clock className="h-4 w-4 text-teal-400" />
                        <span>5 min read</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <BookOpen className="h-4 w-4 text-teal-400" />
                        <span>{todaysBlog.author}</span>
                      </div>
                    </div>

                    {/* Tags */}
                    <div className="flex flex-wrap gap-2 mb-4">
                      {todaysBlog.tags.map((tag, idx) => (
                        <span key={idx} className="text-gray-400 text-sm">#{tag}</span>
                      ))}
                    </div>

                    <div className="text-teal-400 font-medium flex items-center space-x-2 group hover:text-teal-300">
                      <span>Read Full Article</span>
                      <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>
                </div>
              </Link>
            ) : (
              <div className="text-center py-12 text-gray-400">
                <p>No insights available at the moment.</p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Blog Posts Section */}
      <section className="py-20 bg-gray-900" style={{ minHeight: '600px' }}>
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between mb-12">
            <div>
              <h2 className="text-3xl font-bold text-white mb-2 fade-in-up">Latest Blog Posts</h2>
              <p className="text-gray-400">Explore our insights on AI, machine learning, and digital transformation</p>
            </div>
            <BookOpen className="h-12 w-12 text-teal-400" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredBlogs.length > 0 ? (
              filteredBlogs.map((blog) => (
              <Link
                key={blog.id}
                to={`/insights/${blog.slug}`}
                className="bg-gray-800/50 backdrop-blur-sm rounded-xl overflow-hidden border border-gray-700/50 hover:border-teal-400/50 hover:shadow-lg hover:shadow-teal-400/10 transition-all duration-300 group"
              >
                {/* Featured Image */}
                <div className="h-48 overflow-hidden relative bg-gray-800">
                  <img
                    src={blog.featuredImage}
                    alt={blog.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                    onError={(e) => {
                      e.currentTarget.onerror = null;
                      e.currentTarget.src = 'https://images.pexels.com/photos/8386440/pexels-photo-8386440.jpeg?auto=compress&cs=tinysrgb&w=1920&h=1080';
                    }}
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-gray-900/90 via-gray-900/50 to-transparent" />
                  <div className="absolute top-4 left-4">
                    <span className="px-3 py-1 bg-[rgba(20,184,166,0.15)] backdrop-blur-sm text-teal-400 rounded-full text-sm font-medium border border-teal-400/30">
                      {blog.category}
                    </span>
                  </div>
                </div>

                {/* Content */}
                <div className="p-6">
                  {/* Meta Info */}
                  <div className="flex items-center gap-4 text-gray-400 text-sm mb-3">
                    <div className="flex items-center space-x-1">
                      <Calendar className="h-4 w-4" />
                      <span>{new Date(blog.publishDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <Clock className="h-4 w-4" />
                      <span>{blog.readTime}</span>
                    </div>
                  </div>

                  {/* Title */}
                  <h3 className="text-xl font-bold text-white mb-3 line-clamp-2 group-hover:text-teal-400 transition-colors">
                    {blog.title}
                  </h3>

                  {/* Excerpt */}
                  <p className="text-gray-300 text-sm mb-4 line-clamp-3">
                    {blog.excerpt}
                  </p>

                  {/* Tags */}
                  <div className="flex flex-wrap gap-2 mb-4">
                    {blog.tags.slice(0, 3).map((tag, idx) => (
                      <span key={idx} className="text-xs text-gray-400">
                        #{tag}
                      </span>
                    ))}
                  </div>

                  {/* Read More Link */}
                  <div className="flex items-center text-teal-400 font-medium text-sm group-hover:text-teal-300">
                    <span>Read Article</span>
                    <ArrowRight className="h-4 w-4 ml-2 group-hover:translate-x-1 transition-transform" />
                  </div>
                </div>
              </Link>
              ))
            ) : (
              <div className="col-span-full text-center py-12 text-gray-400">
                <p>No blog posts available.</p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Case Studies Grid */}
      <section className="py-20 bg-black">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-white mb-12 text-center fade-in-up">
            Latest Insights ({filteredInsights.length})
          </h2>
          
          {filteredInsights.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-400 text-lg">No insights found matching your criteria.</p>
              <button
                onClick={() => {
                  setSearchTerm('');
                  setSelectedCategory('All');
                  setSelectedTags([]);
                  setFilteredInsights(caseStudies);
                }}
                className="mt-4 text-teal-400 hover:text-teal-300 transition-colors text-link"
              >
                Clear all filters
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {filteredInsights.map((study, index) => (
                <div
                  key={index}
                  className="bg-gray-900/50 backdrop-blur-sm rounded-2xl shadow-xl overflow-hidden
                    border border-gray-800/50 hover:bg-gray-900/70 transition-all duration-300 card-hover fade-in-up"
                  data-category={study.category}
                  data-tags={study.tags.join(',')}
                >
                  <div className="md:flex">
                    <div className="md:w-2/5">
                      <div className="h-64 md:h-full relative">
                        <img
                          src={study.image}
                          alt={study.title}
                          className="w-full h-full object-cover"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent" />
                        <div className="absolute bottom-4 left-4">
                          <div className="p-3 bg-gray-900/50 backdrop-blur-sm rounded-lg shadow-lg
                            border border-gray-800/50">
                            <study.icon size={24} className="text-teal-400" />
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="md:w-3/5 p-8">
                      <div className="flex flex-wrap gap-2 mb-3">
                        {study.tags.map((tag, idx) => (
                          <span key={idx} className="text-gray-400 text-sm">#{tag}</span>
                        ))}
                      </div>
                      <h3 className="text-2xl font-bold text-white mb-4">{study.title}</h3>
                      <div className="space-y-4">
                        <div>
                          <h4 className="text-lg font-semibold text-teal-400 mb-2">Client</h4>
                          <p className="text-gray-300">{study.client}</p>
                        </div>
                        <div>
                          <h4 className="text-lg font-semibold text-teal-400 mb-2">Challenge</h4>
                          <p className="text-gray-300">{study.challenge}</p>
                        </div>
                        <div>
                          <h4 className="text-lg font-semibold text-teal-400 mb-2">Solution</h4>
                          <p className="text-gray-300">{study.solution}</p>
                        </div>
                        <div>
                          <h4 className="text-lg font-semibold text-teal-400 mb-2">Key Results</h4>
                          <ul className="space-y-2">
                            {study.results.slice(0, 3).map((result, idx) => (
                              <li key={idx} className="flex items-start space-x-2 text-gray-300">
                                <ArrowRight size={20} className="flex-shrink-0 mt-1 text-teal-400" />
                                <span>{result}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                        
                        {/* Author Bio */}
                        <div className="flex items-center space-x-3 p-3 bg-gray-800/30 rounded-lg">
                          <img
                            src={study.author.avatar}
                            alt={study.author.name}
                            className="w-10 h-10 rounded-full object-cover"
                          />
                          <div>
                            <h5 className="text-white font-medium text-sm">{study.author.name}</h5>
                            <p className="text-teal-400 text-xs">{study.author.role}</p>
                          </div>
                        </div>

                        {/* Social Share */}
                        <div className="flex items-center space-x-4 pt-4 border-t border-gray-700">
                          <span className="text-gray-400 text-sm">Share:</span>
                          <div className="flex space-x-3">
                            <a href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(study.title)}&url=${encodeURIComponent(window.location.href)}`} 
                               target="_blank" rel="noopener noreferrer"
                               className="text-gray-400 hover:text-blue-400 transition-colors">
                              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z"/>
                              </svg>
                            </a>
                            <a href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(window.location.href)}`}
                               target="_blank" rel="noopener noreferrer"
                               className="text-gray-400 hover:text-blue-600 transition-colors">
                              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                              </svg>
                            </a>
                          </div>
                        </div>

                        <button className="text-teal-400 font-medium flex items-center space-x-2 group hover:text-teal-300 text-link">
                          <span>Read Full {study.title}</span>
                          <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-20 bg-gradient-to-r from-blue-900 to-black">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="max-w-3xl mx-auto fade-in-up">
            <p className="text-xl text-gray-300 mb-8">
              Want to learn more about how our solutions can transform your business? Let's discuss 
              your specific challenges and opportunities.
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center space-x-3 bg-teal-500 text-white px-8 py-4 
                rounded-md text-lg font-medium hover:bg-teal-400 transition-all duration-300 group btn-primary"
            >
              <span>Contact Us Today</span>
              <ArrowRight className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>
      </section>
    </div>
    </>
  );
};

export default Insights;